import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Mail, Phone, Building2, Calendar, Search, Users, MessageSquare, Lock, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "techshore_admin_token";

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}

function Login({ onAuth }) {
  const [token, setToken] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/admin/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.setItem(TOKEN_KEY, token);
      onAuth(token);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Invalid token. Please try again." : "Could not verify. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-paper">
      <div className="w-full max-w-md">
        <a href="/" className="wordmark mb-10 inline-block" data-testid="admin-login-brand">
          TechShore<span className="accent"> Global</span>
        </a>
        <div className="card-flat p-8">
          <div className="w-12 h-12 rounded-full bg-coral-tint text-coral flex items-center justify-center mb-5">
            <Lock size={20} />
          </div>
          <h1 className="text-[28px] text-ink font-semibold leading-tight">Admin sign-in</h1>
          <p className="text-[14px] text-grey-600 mt-2 font-light">
            Enter your admin token to view received leads.
          </p>
          <form onSubmit={submit} className="mt-7" data-testid="admin-login-form">
            <label className="field-label">Admin token</label>
            <input
              type="password"
              className="field"
              placeholder="•••••••••••"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              autoFocus
              data-testid="admin-token-input"
            />
            {err && (
              <div className="mt-3 text-[13px] text-coral-3 bg-coral-tint border border-coral-soft rounded px-3 py-2 font-medium" data-testid="admin-login-error">
                {err}
              </div>
            )}
            <button type="submit" disabled={busy || !token} className="btn-primary w-full justify-center mt-6" data-testid="admin-login-submit">
              {busy ? "Verifying..." : "Sign in"} <ArrowRight size={16} />
            </button>
          </form>
        </div>
        <p className="text-[12px] text-grey-500 mt-6 text-center font-light">
          This panel is for internal use only.
        </p>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="card-flat p-6 flex items-center gap-4">
      <div className="w-11 h-11 rounded-lg bg-coral-tint text-coral flex items-center justify-center">{icon}</div>
      <div>
        <div className="text-[28px] text-ink font-semibold leading-none">{value}</div>
        <div className="text-[12px] uppercase tracking-[0.14em] text-grey-600 mt-1.5 font-semibold">{label}</div>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("consultations");
  const [consultations, setConsultations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API}/consultations`, { headers }),
        axios.get(`${API}/contacts`, { headers }),
      ]);
      setConsultations(c.data || []);
      setContacts(m.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
      } else {
        setErr("Could not load leads. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filter = (rows, fields) => {
    if (!q) return rows;
    const ql = q.toLowerCase();
    return rows.filter((r) => fields.some((f) => (r[f] || "").toString().toLowerCase().includes(ql)));
  };

  const consultationRows = filter(consultations, ["name", "email", "company", "service", "team_size", "message"]);
  const contactRows = filter(contacts, ["name", "email", "subject", "message"]);

  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <header className="bg-white border-b border-grey-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="wordmark" data-testid="admin-brand">
              TechShore<span className="accent"> Global</span>
            </a>
            <span className="text-[12px] uppercase tracking-[0.18em] text-grey-500 font-bold border-l border-grey-300 pl-4">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} disabled={loading} className="btn-outline !py-2 !px-4 !text-[12px]" data-testid="admin-refresh-btn">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={onLogout} className="btn-outline !py-2 !px-4 !text-[12px]" data-testid="admin-logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="text-[36px] lg:text-[44px] text-ink font-semibold tracking-[-0.025em] leading-tight">
          Leads dashboard
        </h1>
        <p className="text-[15px] text-grey-600 mt-2 font-light">
          All consultations and contact-form submissions, freshest first.
        </p>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <StatCard label="Consultations" value={consultations.length} icon={<Users size={18} />} />
          <StatCard label="Contacts" value={contacts.length} icon={<MessageSquare size={18} />} />
          <StatCard label="Total leads" value={consultations.length + contacts.length} icon={<Mail size={18} />} />
        </div>

        {/* Toolbar */}
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex gap-1 bg-white border border-grey-200 rounded-full p-1">
            <button
              onClick={() => setTab("consultations")}
              className={`px-5 py-2 rounded-full text-[13px] font-semibold transition ${
                tab === "consultations" ? "bg-coral text-white" : "text-grey-700 hover:text-ink"
              }`}
              data-testid="tab-consultations"
            >
              Consultations <span className="opacity-70 ml-1">({consultations.length})</span>
            </button>
            <button
              onClick={() => setTab("contacts")}
              className={`px-5 py-2 rounded-full text-[13px] font-semibold transition ${
                tab === "contacts" ? "bg-coral text-white" : "text-grey-700 hover:text-ink"
              }`}
              data-testid="tab-contacts"
            >
              Contacts <span className="opacity-70 ml-1">({contacts.length})</span>
            </button>
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-grey-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, company..."
              className="field pl-10 w-full sm:w-80"
              data-testid="admin-search-input"
            />
          </div>
        </div>

        {err && <div className="mt-6 text-[13.5px] text-coral-3 bg-coral-tint border border-coral-soft rounded px-4 py-2.5 font-medium" data-testid="admin-error">{err}</div>}

        {/* Table */}
        <div className="mt-6 card-flat overflow-hidden">
          {tab === "consultations" ? (
            <ConsultationsTable rows={consultationRows} loading={loading} onRowClick={setActive} />
          ) : (
            <ContactsTable rows={contactRows} loading={loading} onRowClick={setActive} />
          )}
        </div>
      </main>

      {active && <DetailDrawer item={active} type={tab} onClose={() => setActive(null)} />}
    </div>
  );
}

function ConsultationsTable({ rows, loading, onRowClick }) {
  if (loading) return <EmptyState>Loading...</EmptyState>;
  if (!rows.length) return <EmptyState>No consultations yet.</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[14px]" data-testid="consultations-table">
        <thead className="bg-grey-100 border-b border-grey-200">
          <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-grey-600 font-bold">
            <th className="px-5 py-3">Name</th>
            <th className="px-5 py-3">Company</th>
            <th className="px-5 py-3">Capability</th>
            <th className="px-5 py-3">Team size</th>
            <th className="px-5 py-3">Preferred</th>
            <th className="px-5 py-3">Received</th>
            <th className="px-5 py-3 w-12"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => onRowClick({ ...r, _kind: "consultation" })}
              className="border-b border-grey-100 hover:bg-coral-tint/40 cursor-pointer transition"
              data-testid={`consultation-row-${r.id}`}
            >
              <td className="px-5 py-4">
                <div className="font-semibold text-ink">{r.name}</div>
                <div className="text-grey-500 text-[12.5px] font-light">{r.email}</div>
              </td>
              <td className="px-5 py-4 text-grey-700 font-light">{r.company || "—"}</td>
              <td className="px-5 py-4 text-grey-700 font-light">{r.service || "—"}</td>
              <td className="px-5 py-4 text-grey-700 font-light">{r.team_size || "—"}</td>
              <td className="px-5 py-4 text-grey-700 font-light">
                {r.preferred_date || "—"}{r.preferred_time ? ` · ${r.preferred_time}` : ""}
              </td>
              <td className="px-5 py-4 text-grey-500 font-light text-[13px]">{fmtDate(r.created_at)}</td>
              <td className="px-5 py-4 text-coral">→</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactsTable({ rows, loading, onRowClick }) {
  if (loading) return <EmptyState>Loading...</EmptyState>;
  if (!rows.length) return <EmptyState>No contact messages yet.</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[14px]" data-testid="contacts-table">
        <thead className="bg-grey-100 border-b border-grey-200">
          <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-grey-600 font-bold">
            <th className="px-5 py-3">Name</th>
            <th className="px-5 py-3">Subject</th>
            <th className="px-5 py-3">Message</th>
            <th className="px-5 py-3">Received</th>
            <th className="px-5 py-3 w-12"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => onRowClick({ ...r, _kind: "contact" })}
              className="border-b border-grey-100 hover:bg-coral-tint/40 cursor-pointer transition"
              data-testid={`contact-row-${r.id}`}
            >
              <td className="px-5 py-4">
                <div className="font-semibold text-ink">{r.name}</div>
                <div className="text-grey-500 text-[12.5px] font-light">{r.email}</div>
              </td>
              <td className="px-5 py-4 text-grey-700 font-light">{r.subject || "—"}</td>
              <td className="px-5 py-4 text-grey-700 font-light max-w-md truncate">{r.message || "—"}</td>
              <td className="px-5 py-4 text-grey-500 font-light text-[13px]">{fmtDate(r.created_at)}</td>
              <td className="px-5 py-4 text-coral">→</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ children }) {
  return <div className="px-8 py-16 text-center text-grey-500 font-light">{children}</div>;
}

function DetailDrawer({ item, type, onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="lead-detail-drawer">
      <div className="modal-card !max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold">
              {item._kind === "consultation" ? "Consultation" : "Contact"}
            </div>
            <h3 className="text-[26px] text-ink font-semibold leading-tight mt-1">{item.name}</h3>
            <a href={`mailto:${item.email}`} className="text-[14px] text-grey-600 hover:text-coral inline-flex items-center gap-2 mt-1 font-light">
              <Mail size={13} /> {item.email}
            </a>
          </div>
          <button onClick={onClose} className="text-grey-500 hover:text-ink p-2" data-testid="lead-detail-close">✕</button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          {item.company && (
            <Field label="Company" value={item.company} icon={<Building2 size={13} />} />
          )}
          {item.phone && (
            <Field label="Phone" value={item.phone} icon={<Phone size={13} />} />
          )}
          {item.team_size && <Field label="Team size" value={item.team_size} />}
          {item.service && <Field label="Capability" value={item.service} />}
          {item.subject && <Field label="Subject" value={item.subject} />}
          {item.preferred_date && (
            <Field label="Preferred date" value={`${item.preferred_date}${item.preferred_time ? ` · ${item.preferred_time}` : ""}`} icon={<Calendar size={13} />} />
          )}
          <Field label="Received" value={fmtDate(item.created_at)} />
          <Field label="Email sent" value={item.email_sent ? "Yes" : "No (Resend not configured)"} />
        </div>

        {item.message && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-grey-600 mb-2 font-bold">Message</div>
            <div className="text-[14.5px] text-grey-700 bg-paper rounded-lg p-4 whitespace-pre-wrap font-light leading-[1.65]">
              {item.message}
            </div>
          </div>
        )}

        <div className="mt-7 pt-5 border-t border-grey-200 flex items-center justify-between gap-3">
          <span className="text-[11px] font-mono text-grey-500">ID: {item.id}</span>
          <a href={`mailto:${item.email}?subject=Re: your TechShore Global enquiry`} className="btn-primary !py-2.5 !px-5 !text-[12.5px]" data-testid="lead-reply-btn">
            Reply by email <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-grey-600 mb-1.5 font-bold">{label}</div>
      <div className="text-[14.5px] text-ink font-medium flex items-center gap-2">
        {icon && <span className="text-coral">{icon}</span>}
        {value}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(!!token);

  // Verify stored token on mount
  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.post(`${API}/admin/verify`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setVerified(true))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); })
      .finally(() => setChecking(false));
  }, [token]);

  const handleAuth = (t) => { setToken(t); setVerified(true); };
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setVerified(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="text-grey-500 font-light">Loading...</div>
      </div>
    );
  }

  if (!verified) return <Login onAuth={handleAuth} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
}
